// Getting call audio back after the network drops during a call.
//
// The browser and Asterisk check every few seconds that the audio path still
// works (ICE). After a short drop (a few seconds) the same path simply works
// again. After a longer one (about 10 s or more; Wi-Fi off, laptop changes
// network) one or both sides give up on that path: the call stays up but no
// audio flows in either direction (lab: 20 s of blocked UDP → silent for good;
// hold/resume sometimes helped by chance). The fix every softphone uses is an
// "ICE restart": a re-INVITE with new ICE credentials, so both sides look for a
// working path again. Pure decisions here; the Phone engine does the work.
//
// Rules (owner tests, session 13):
// - Only when audio really stopped: Chrome can say "disconnected" while audio
//   still flows on a lossy network; restarting then only makes things worse.
// - Only once SIP has been proven to work again (a REGISTER answered after the
//   drop began): a re-INVITE sent on a dead WebSocket fails, and JsSIP then
//   ends the call (seen in a 20 s Wi-Fi test).
// - Back off when restarts keep being needed (a flapping network), and count
//   a path as healthy again only after it has stayed up for a while.

export type IceState = RTCIceConnectionState;

/** The path has been bad this long (short blips heal by themselves first). */
export const RESTART_AFTER_MS = 4000;
/** ...and no audio packet has arrived for this long. */
export const NO_AUDIO_MS = 3000;
/** Wait between restarts: steady at first (audio is gone, so every attempt counts), slower later. */
export const RETRY_EVERY_MS = 8000;
export const MAX_RETRY_MS = 15_000;
/** Give up after this many restarts in a row (the call ends on its own if the network never returns). */
export const MAX_RESTARTS = 8;
/** A path that stays up this long counts as healthy again (restart count back to 0). */
export const STABLE_MS = 30_000;

export interface IceWatch {
  /** Date.now() when the path went bad (null while it works) */
  badSince: number | null;
  /** Date.now() when it last became connected (null while bad) */
  upSince: number | null;
  restarts: number;
  lastRestartAt: number;
  /** audio packets received so far, and when that number last went up */
  rx: number | null;
  rxAt: number;
  /** bad because audio stopped while ICE still says "connected" (see onSilence) */
  silent: boolean;
}

export function newIceWatch(): IceWatch {
  return { badSince: null, upSince: null, restarts: 0, lastRestartAt: 0, rx: null, rxAt: 0, silent: false };
}

/** Update the watch for a new ICE state. */
export function onIceState(w: IceWatch, state: IceState, now: number): IceWatch {
  // "connected" says nothing about a silent path: only audio arriving again clears that (onPackets)
  if (state === 'connected' || state === 'completed') return w.silent ? w : { ...w, badSince: null, upSince: w.upSince ?? now };
  if (state === 'disconnected' || state === 'failed') {
    if (w.badSince !== null) return w;
    // a path that stayed up long enough: earlier restarts don't count against this drop
    const healthy = w.upSince !== null && now - w.upSince >= STABLE_MS;
    return { ...w, badSince: now, upSince: null, restarts: healthy ? 0 : w.restarts, rx: null };
  }
  return w; // new / checking / closed: no change
}

/** Record the audio packet counter (read from getStats while the path is bad). */
export function onPackets(w: IceWatch, packets: number, now: number): IceWatch {
  if (w.rx !== null && packets > w.rx && w.silent) return { ...w, rx: packets, rxAt: now, badSince: null, silent: false, upSince: now };
  if (w.rx === null || packets > w.rx) return { ...w, rx: packets, rxAt: now };
  return w;
}

/** No audio packet for this long on a connected, not-held call = the path is dead even if ICE says "connected". */
export const SILENT_MS = 6000;

/**
 * Owner test (session 13): after a 20 s Wi-Fi drop both sides heard nothing
 * while ICE still reported "connected" (nothing for the ICE watch to react
 * to); only hold/resume (a re-INVITE) brought the audio back. Asterisk sends
 * audio continuously during a call (also silence), so a long gap means the
 * path is dead: treat it like a lost path. `lastRxAt` = when packets last
 * arrived (from the call's own audio counter).
 */
export function onSilence(w: IceWatch, packets: number, lastRxAt: number, now: number, held: boolean): IceWatch {
  if (held || w.badSince !== null || now - lastRxAt < SILENT_MS) return w;
  return { ...w, badSince: lastRxAt, upSince: null, silent: true, rx: packets, rxAt: lastRxAt };
}

/** The path is bad and audio has stopped: worth telling the user and fixing. */
export function audioLost(w: IceWatch, now: number): boolean {
  return w.badSince !== null && w.rx !== null && now - w.rxAt >= NO_AUDIO_MS;
}

export function retryWait(restarts: number): number {
  return restarts < 4 ? RETRY_EVERY_MS : MAX_RETRY_MS;
}

/**
 * From the second attempt on, look for a path through our TURN server only.
 * Owner test (session 13): after a 20 s Wi-Fi drop every direct restart
 * "connected" for a moment and then failed again (home router NAT after the
 * Wi-Fi reconnect), while the call never got audio back. The TURN server runs
 * on the same machine as Asterisk, so a relayed path costs no extra distance
 * and doesn't depend on the router's NAT behaving.
 */
export function relayOnly(restarts: number, haveTurn: boolean): boolean {
  return haveTurn && restarts >= 1;
}

export type IceAction = 'restart' | 'check_sip' | 'wait';

/**
 * - restart: send the ICE-restart re-INVITE now
 * - check_sip: first prove SIP works again (send a REGISTER; restart once it's answered)
 */
export function iceAction(
  w: IceWatch,
  state: IceState,
  now: number,
  opts: { callUp: boolean; sipConnected: boolean; registeredAt: number },
): IceAction {
  if (!opts.callUp || w.badSince === null || !audioLost(w, now)) return 'wait';
  if (w.restarts >= MAX_RESTARTS) return 'wait';
  if (state !== 'failed' && now - w.badSince < RESTART_AFTER_MS) return 'wait';
  if (w.restarts > 0 && now - w.lastRestartAt < retryWait(w.restarts)) return 'wait';
  if (!opts.sipConnected || opts.registeredAt < w.badSince) return 'check_sip';
  return 'restart';
}

/** JsSIP end causes that mean "this device lost the call": its BYE probably never reached the server. */
export function isLostCause(cause: string | undefined): boolean {
  return cause === 'Connection Error' || cause === 'Request Timeout' || cause === 'Dialog Error' || cause === 'RTP Timeout';
}
